/* TARAMAR: Dawn'i cart drawer on modaal — klõps avab, X või kate sulgeb, fookus
   lukustatakse paneeli sisse ja keha scroll pannakse kinni. Meil on see hoopis
   hover-eelvaade ostukorvi ikooni all. Muudetud on kolm asja:

   1. AVAMINE. Osutiga seadmel mouseenter/mouseleave (~250 ms viivitus, et hiir
      jõuaks ikoonilt paneelile ilma et see vahepeal sulguks). Puutega seadmel ei
      ole hoveri't olemas — seal tapib ikoon eelvaate lahti/kinni.
   2. FOOKUS. Fookuselõksu ei ole. Lõks eeldab, et kasutaja SISENES paneeli
      teadlikult ja saab sealt ka välja; hoveril avanev paneel, mis fookuse endasse
      lukustab, on klaviatuurikasutajale lõks päris tähenduses. Klaviatuuriga
      avaneb eelvaade fookusel ja sulgub, kui fookus paneelist välja liigub.
   3. KEHA SCROLL. `overflow-hidden` on eemaldatud — hover-eelvaade ei tohi lehe
      kerimist blokeerida. Just see tegi Dawn'i variandist modaali. */

const CART_PREVIEW_CLOSE_DELAY = 250;
// Sulgemisviivitus PÄRAST toote lisamist — pikem kui hover-eelvaate oma
// (CART_PREVIEW_CLOSE_DELAY käib hiire mouseleave'i järel, see siin
// avaneb ISESEISVALT lisamise peale). 4s -> 8s (17.08.2026, sinu otsus):
// klient, kes parasjagu ei vaadanud ekraani lisamise hetkel, ei jõudnud
// 4 sekundiga aru saada, kas toode üldse lisandus. Kinnitussõnum
// (showAddedConfirmation) annab sama info nähtavamalt, aga aken jääb
// pikemaks ka neile, kes loevad.

const CART_ADD_CLOSE_DELAY = 8000;
const CART_ADD_CONFIRMATION_DURATION = 2600;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeTimer = null;
    // Sama test, mille CSS-i `@media (hover: hover)` teeks: kas seadmel on päris
    // osuti. Puuteekraanid raporteerivad `hover: none`, mistõttu neil läheb
    // käiku tap-toggle allpool.
    this.hasHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindPreviewBehaviour();
  }

  get cartLink() {
    return document.querySelector('#cart-icon-bubble');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = this.cartLink;
    if (!cartLink) return;

    // NB: `role="button"` on siit teadlikult ära jäetud. Osutiga seadmel jääb see
    // päris lingiks /cart peale — klõps VIIB ostukorvi, hover ainult näitab.
    // Nii ei kaota me klaviatuuri- ega keskklõpsu/uues-sakis-avamise käitumist.
    cartLink.setAttribute('aria-haspopup', 'true');
    cartLink.setAttribute('aria-expanded', 'false');
  }

  bindPreviewBehaviour() {
    const cartLink = this.cartLink;
    if (!cartLink) return;

    if (this.hasHover) {
      for (const el of [cartLink, this]) {
        el.addEventListener('mouseenter', () => this.cancelClose() || this.open(cartLink));
        el.addEventListener('mouseleave', () => this.scheduleClose());
      }

      // Klaviatuur: fookus ikoonile avab, fookuse lahkumine paneelist sulgeb.
      cartLink.addEventListener('focus', () => this.open(cartLink));
      document.addEventListener('focusin', (event) => {
        if (!this.classList.contains('active')) return;
        if (this.contains(event.target) || event.target === cartLink) return;
        this.close();
      });
    } else {
      // Puuteseade: tap avab/sulgeb, ei navigeeri. Ostukorvi lehele saab
      // paneeli sees oleva „Vaata ostukorvi" nupuga.
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      });

      // Tap mujale lehel sulgeb.
      document.addEventListener('click', (event) => {
        if (!this.classList.contains('active')) return;
        if (this.contains(event.target) || cartLink.contains(event.target)) return;
        this.close();
      });
    }
  }

  cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    // Tagastab falsy, et `this.cancelClose() || this.open(...)` alati avaks.
    return false;
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.close(), CART_PREVIEW_CLOSE_DELAY);
  }

  open(triggeredBy) {
    this.cancelClose();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.cartLink?.setAttribute('aria-expanded', 'true');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.cancelClose();
    this.classList.remove('active');
    this.cartLink?.setAttribute('aria-expanded', 'false');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      // „Lisa ostukorvi" avab eelvaate, et lisamine oleks näha. Aga kuna hiir on
      // sel hetkel tootelehe nupul, mitte eelvaatel, ei tuleks mouseleave'i
      // kunagi ja paneel jääks igaveseks lahti — seega sulgeme ise.
      this.open();
      this.showAddedConfirmation();
      this.closeTimer = setTimeout(() => this.close(), CART_ADD_CLOSE_DELAY);
    });
  }

  /* Kinnitusteade "Lisatud ostukorvi" (17.08.2026, sinu otsus KV-1).
     Klient soovis JÄTKUVALT ainult ühte tegevust eelvaates ("Vaata
     ostukorvi", ei kassanuppu) — see siin ei lisa tegevust, ainult
     tagasisidet. Lisatakse .drawer__header sisse PÄRAST seda, kui
     #CartDrawer on juba täies mahus asendatud (getSectionsToRender
     asendab kogu #CartDrawer, sh header'i, seega päris HTML tuleb
     server poolt tagasi ilma teateta — teade on puhtalt kliendipoolne
     lisandus). renderContents() käib AINULT toote lisamise järel
     (vt product-form.js), mitte hover-avamisel, seega ei pea siin
     kontrollima, kas see on "lisamise" avamine — see ongi alati see. */
  showAddedConfirmation() {
    const header = this.querySelector('.drawer__header');
    if (!header || !window.cartStrings?.itemAddedConfirmation) return;

    header.querySelector('.cart-drawer__added-confirmation')?.remove();

    const note = document.createElement('p');
    note.className = 'cart-drawer__added-confirmation';
    note.setAttribute('role', 'status');
    note.textContent = window.cartStrings.itemAddedConfirmation;
    header.appendChild(note);

    setTimeout(() => note.remove(), CART_ADD_CONFIRMATION_DURATION);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
